import type { EventRow } from '@lib/care-model';

/**
 * THE QUEUE ROW'S MESSAGE LINE (William, 16 Oct 2026 about 8:30 PM, verbatim): "On the second line of each -
 * show the sms message text (it can be cut off, the voice call, or the email sufficicent to make a decision
 * (ideally) from from looking at the row. and maybe add an additional row - so one can have up to 2 lines.
 * So, I can just look at the line and make the call."
 *
 * The words come from the list row the page already holds (subject and the 160-character `preview` the
 * server's list projection has always sent); nothing new is read. The line is a glance, never the case:
 * the full text is the line's tooltip, and the case still opens on a click.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

export type PreviewSource = Pick<EventRow, 'channel' | 'subject' | 'preview' | 'hasAudio'>;

export interface MessagePreview {
  /** What kind of message it is, said first ("Text", "Voicemail", "Call", "Email"). */
  lead: string;
  /** The words, whitespace collapsed; '' when the entry carries none. */
  text: string;
}

/** Said in place of the words when an entry carries none, so the line is never blank or "undefined". */
export const NO_TEXT_WORDING = '(no message text)';

/**
 * Subjects a relay or a feed writes, which say nothing the member said ("Inbound SMS to +1833... from
 * +1209...", "Text reply", "Voicemail from ..."). An SMS or a call never shows its subject; an email shows
 * it unless it is one of these.
 */
const MACHINE_SUBJECT = /^(inbound sms to |text reply$|voicemail( from|$)|missed call|new voicemail)/i;

/** Lines that start an email's quoted history or its signature: everything from there on is dropped. */
const EMAIL_CUT_LINE = [
  /^\s*>/,
  /^\s*On .{0,120}wrote:\s*$/i,
  /^\s*-{2,}\s*(original message|forwarded message)/i,
  /^\s*_{5,}\s*$/,
  /^\s*From:\s/i,
  /^\s*Sent from my /i,
  /^\s*--\s*$/,
];

/** The same cut once the text sits on one line (a body the feed already flattened). */
const EMAIL_CUT_INLINE = /\s(On \S[\s\S]{0,120}? wrote:|-{2,}\s*Original Message|From: \S)/i;

const collapse = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** PART O: a transcript line (a speaker or the system, then a colon), anywhere in a flattened preview too. */
const TRANSCRIPT_AT = /(^|\s)(system|claire|caller|agent|assistant|user|member|operator)\s*:/i;

/** A call's summary: the words before its first transcript line. */
export function callSummaryText(raw: string): string {
  const flat = collapse(raw);
  const m = TRANSCRIPT_AT.exec(flat);
  return m ? flat.slice(0, m.index).trim() : flat;
}

/** An email body with its quoted history and signature cut off, as far as a cheap read can tell. */
export function emailBody(raw: string): string {
  const kept: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (EMAIL_CUT_LINE.some((re) => re.test(line))) break;
    kept.push(line);
  }
  const flat = collapse(kept.join(' '));
  const m = EMAIL_CUT_INLINE.exec(flat);
  return m ? flat.slice(0, m.index).trim() : flat;
}

export function messagePreview(r: PreviewSource): MessagePreview {
  const subject = collapse(r.subject ?? '');
  const body = r.preview ?? '';
  switch (r.channel) {
    case 'SMS':
      return { lead: 'Text', text: collapse(body) };
    case 'Voice':
      // PART O: a call previews its summary and reason only, never a transcript line or the intake JSON
      // (an older server sent the content's first 160 characters, transcript and all).
      return { lead: r.hasAudio ? 'Voicemail' : 'Call', text: callSummaryText(body) };
    case 'Email': {
      const words = emailBody(body);
      const subj = subject && !MACHINE_SUBJECT.test(subject) ? subject : '';
      return { lead: 'Email', text: subj && words ? `${subj} - ${words}` : subj || words };
    }
    default:
      return { lead: 'Message', text: collapse(body) || (MACHINE_SUBJECT.test(subject) ? '' : subject) };
  }
}

/** The whole line as one string ("Text: I need a new kit"), for the tooltip and for tests. */
export function previewLine(p: MessagePreview): string {
  return `${p.lead}: ${p.text || NO_TEXT_WORDING}`;
}
